import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createDebugLogger } from '@videa/utils';

const debug = createDebugLogger('STT');

// Web Speech API types (note: this is not fully typed in TS lib)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error:
    | 'no-speech'
    | 'aborted'
    | 'audio-capture'
    | 'network'
    | 'not-allowed'
    | 'service-not-allowed'
    | 'bad-grammar'
    | 'language-not-supported';
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  // Recognition parameters
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;

  // Experimental: On-device processing (Safari/WebKit)
  processLocally?: boolean;

  // Service URI (deprecated but still exists)
  serviceURI?: string;

  // Control methods
  start(): void;
  stop(): void;
  abort(): void;

  // Event handlers
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: ((event: Event) => void) | null;
  onstart: ((event: Event) => void) | null;
  onaudiostart: ((event: Event) => void) | null;
  onaudioend: ((event: Event) => void) | null;
  onsoundstart: ((event: Event) => void) | null;
  onsoundend: ((event: Event) => void) | null;
  onspeechstart: ((event: Event) => void) | null;
  onspeechend: ((event: Event) => void) | null;
  onnomatch: ((event: SpeechRecognitionEvent) => void) | null;
}

// Modern SpeechRecognition constructor with static methods (Chrome 123+)
interface SpeechRecognitionStatic {
  new (): SpeechRecognition;
  available?(options: {
    langs: string[];
    processLocally?: boolean;
  }): Promise<
    'available' | 'download-required' | 'unavailable' | 'downloadable'
  >;
  install?(options: {
    langs: string[];
    processLocally?: boolean;
  }): Promise<boolean>;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionStatic;
    webkitSpeechRecognition: SpeechRecognitionStatic;
  }
}

export interface UseLiveTranscriptionOptions {
  /** Language for recognition (default: 'en-US') */
  lang?: string;
  /** Whether to show interim (in-progress) results */
  interimResults?: boolean;
  /** Max transcript lines to keep (default: 5) */
  maxLines?: number;
  /** Whether recognition should continue after user stops speaking */
  continuous?: boolean;
  /** Maximum alternatives per result (default: 1) */
  maxAlternatives?: number;
  /**
   * Force local processing only (Chrome/Edge/Opera support)
   * When true, speech recognition will only work if browser supports local processing
   * When false or unsupported, recognition will be disabled to protect customer privacy
   *
   * @default true
   */
  requireLocalProcessing?: boolean;
}

export interface UseLiveTranscriptionReturn {
  /** Whether Speech Recognition is supported WITH local processing */
  isSupported: boolean;
  /** Whether local processing is available and enabled */
  isLocalProcessing: boolean;
  /** Whether recognition is currently active */
  isListening: boolean;
  /** Whether audio has started being captured */
  isAudioStarted: boolean;
  /** Whether speech has been detected */
  isSpeechStarted: boolean;
  /** Current interim transcript (in-progress speech) */
  interimTranscript: string;
  /** Recent final transcripts */
  recentTranscripts: string[];
  /** All recognition alternatives from the last result */
  alternatives: SpeechRecognitionAlternative[];
  /** Start listening */
  startListening: () => Promise<void>;
  /** Stop listening */
  stopListening: () => void;
  /** Abort listening (immediate stop without processing pending audio) */
  abortListening: () => void;
  /** Clear transcript history */
  clearTranscripts: () => void;
  /** Error details if recognition failed */
  error: {
    type: string;
    message: string;
  } | null;
}

/**
 * Hook for browser-based live speech-to-text using the {@link https://developer.mozilla.org/en-US/docs/Web/API/SpeechRecognition | Web Speech API}.
 *
 * Works offline and provides real-time feedback during recording.
 *
 * ⚠️ This is intended for preview only - not a replacement for the backend
 * transcription which uses a more accurate model. ⚠️
 *
 * ## Browser Support & Privacy
 *
 * - **Local processing only**: Chrome, Edge, Opera (when `processLocally: true`)
 * - **Privacy-first**: This hook ONLY works with local processing to protect customer data
 * - **No cloud processing**: Speech recognition is disabled if local processing unavailable
 * - **Safari**: Uses webkit prefix but does NOT support `processLocally` (cloud-only)
 *
 * Note: Do not confuse browser Web Speech API with iOS Speech Framework (`SFSpeechRecognitionRequest`)
 */
export const useLiveTranscription = (
  options: UseLiveTranscriptionOptions = {},
): UseLiveTranscriptionReturn => {
  const {
    lang = 'en-US',
    interimResults = true,
    maxLines = 5,
    continuous = true,
    maxAlternatives = 1,
    requireLocalProcessing = true, // Default to true
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [isAudioStarted, setIsAudioStarted] = useState(false);
  const [isSpeechStarted, setIsSpeechStarted] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [recentTranscripts, setRecentTranscripts] = useState<string[]>([]);
  const [alternatives, setAlternatives] = useState<
    SpeechRecognitionAlternative[]
  >([]);
  const [error, setError] = useState<{ type: string; message: string } | null>(
    null,
  );
  const [isLocalProcessing, setIsLocalProcessing] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isStoppingRef = useRef(false);
  const isListeningRef = useRef(false);

  // Check if Speech Recognition is supported WITH local processing
  // Only enable if we can guarantee local processing for privacy
  const isSupported = useMemo(() => {
    if (typeof window === 'undefined') return false;

    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return false;

    // If local processing is required, check if browser supports it
    if (requireLocalProcessing) {
      // Test if processLocally property exists (Chrome/Edge/Opera)
      const testInstance = new SpeechRecognitionAPI();
      const supportsLocal = 'processLocally' in testInstance;
      testInstance.abort();
      return supportsLocal;
    }

    return true;
  }, [requireLocalProcessing]);

  // Initialize recognition instance
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionAPI();

    // Configure local processing FIRST (required for privacy)
    if ('processLocally' in recognition) {
      recognition.processLocally = true; // Always enable for privacy
      setIsLocalProcessing(true);
      debug.log('✅ Local processing enabled - customer audio stays on device');
    } else if (requireLocalProcessing) {
      debug.warn(
        '⚠️ Local processing required but not supported - recognition will be disabled for privacy',
      );
      return; // Don't initialize recognition without local processing
    } else {
      debug.warn(
        '⚠️ Local processing not available - audio may be sent to cloud services',
      );
      setIsLocalProcessing(false);
    }

    // Configure basic properties
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.maxAlternatives = maxAlternatives;

    // Set the requested language - availability check in startListening may adjust this
    recognition.lang = lang;

    debug.log('Configured recognition with:', {
      lang: recognition.lang,
      continuous,
      interimResults,
      maxAlternatives,
      processLocally: recognition.processLocally,
      browserLang: navigator.language,
    });

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';
      const currentAlternatives: SpeechRecognitionAlternative[] = [];

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript || '';

        // Collect all alternatives for the current result
        if (result) {
          for (let j = 0; j < result.length && j < maxAlternatives; j++) {
            const alternative = result[j];
            if (alternative) {
              currentAlternatives.push(alternative);
            }
          }
        }

        if (result?.isFinal) {
          final += transcript;
          debug.log('Final transcript:', transcript);
          if (result.length > 1) {
            debug.log(
              'Alternatives:',
              Array.from(
                { length: result.length },
                (_, altIdx) =>
                  `${result[altIdx]?.transcript} (${((result[altIdx]?.confidence ?? 0) * 100).toFixed(1)}%)`,
              ).join(', '),
            );
          }
        } else {
          interim += transcript;
        }
      }

      setInterimTranscript(interim);
      setAlternatives(currentAlternatives);

      if (final) {
        setRecentTranscripts((prev) => {
          const updated = [...prev, final.trim()];
          debug.log('Updated transcripts array:', updated);
          return updated.slice(-maxLines);
        });
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // 'no-speech' and 'aborted' are not real errors
      if (event.error === 'no-speech' || event.error === 'aborted') {
        debug.log('Non-critical error:', event.error);
        return;
      }

      // Add detailed logging for language errors to help debug
      if (event.error === 'language-not-supported') {
        debug.error('Language error details:', {
          requestedLang: lang,
          actualRecognitionLang: recognitionRef.current?.lang,
          processLocally: recognitionRef.current?.processLocally,
          browserLang: navigator.language,
          browserLangs: navigator.languages,
          errorMessage: event.message,
          userAgent: navigator.userAgent,
        });
      }

      const errorMessages: Record<string, string> = {
        'audio-capture':
          'No microphone was found or microphone access was denied',
        network: 'Network error occurred during recognition',
        'not-allowed': 'Permission to use microphone was denied',
        'service-not-allowed': 'Browser does not allow speech recognition',
        'bad-grammar': 'Grammar format error in recognition',
        'language-not-supported':
          'The specified language is not supported for speech recognition',
      };

      const message =
        errorMessages[event.error] || event.message || 'Unknown error';
      setError({ type: event.error, message });
      debug.error('Recognition error:', event.error, message);

      setIsListening(false);
      setIsAudioStarted(false);
      setIsSpeechStarted(false);
      isListeningRef.current = false;
    };

    // Add new event handlers for audio lifecycle
    recognition.onstart = () => {
      debug.log('Recognition started');
      setIsListening(true);
      isListeningRef.current = true;
    };

    recognition.onaudiostart = () => {
      debug.log('Audio capture started');
      setIsAudioStarted(true);
    };

    recognition.onaudioend = () => {
      debug.log('Audio capture ended');
      setIsAudioStarted(false);
    };

    recognition.onspeechstart = () => {
      debug.log('Speech detected');
      setIsSpeechStarted(true);
    };

    recognition.onspeechend = () => {
      debug.log('Speech ended');
      setIsSpeechStarted(false);
    };

    recognition.onnomatch = () => {
      debug.log('No match found for speech input');
      setError({ type: 'no-match', message: 'Speech was not recognized' });
    };

    recognition.onend = () => {
      debug.log('Recognition ended');
      // Auto-restart if we didn't explicitly stop and continuous mode is enabled
      if (isListeningRef.current && !isStoppingRef.current && continuous) {
        try {
          debug.log('Auto-restarting recognition...');
          recognition.start();
        } catch (restartError) {
          // Reset state since recognition has stopped and can't be restarted
          debug.log('Auto-restart failed, resetting state:', restartError);
          setIsListening(false);
          setIsAudioStarted(false);
          setIsSpeechStarted(false);
          isListeningRef.current = false;
          isStoppingRef.current = false;
          setError({
            type: 'aborted',
            message: 'Recognition stopped and auto-restart failed',
          });
        }
      } else {
        setIsListening(false);
        setIsAudioStarted(false);
        setIsSpeechStarted(false);
        isListeningRef.current = false;
        isStoppingRef.current = false;
      }
    };

    recognitionRef.current = recognition;

    return () => {
      // Set flags BEFORE abort() to prevent onend from attempting restart
      isStoppingRef.current = true;
      isListeningRef.current = false;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [
    isSupported,
    lang,
    interimResults,
    maxLines,
    continuous,
    maxAlternatives,
    requireLocalProcessing,
  ]);

  const startListening = useCallback(async () => {
    // Capture recognition instance at start - may change during async operations
    const recognition = recognitionRef.current;
    if (!recognition || !isSupported) {
      debug.warn('Cannot start - recognition not available');
      return;
    }

    // Check language availability for local processing (Chrome 123+)
    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    // Log availability API status
    debug.log('Starting recognition...', {
      hasAvailableAPI: typeof SpeechRecognitionAPI.available === 'function',
      hasInstallAPI: typeof SpeechRecognitionAPI.install === 'function',
      requestedLang: lang,
    });

    // Capture the API functions after type check so TypeScript knows they're defined
    const availableAPI = SpeechRecognitionAPI.available;
    const installAPI = SpeechRecognitionAPI.install;

    if (requireLocalProcessing && typeof availableAPI === 'function') {
      // Helper to check and potentially install a language pack
      const tryLanguage = async (
        targetLang: string,
      ): Promise<'available' | 'unavailable' | 'error'> => {
        try {
          const availability = await availableAPI({
            langs: [targetLang],
            processLocally: true,
          });
          debug.log(`Language availability for '${targetLang}':`, availability);

          if (availability === 'available') {
            return 'available';
          }

          if (
            availability === 'downloadable' ||
            availability === 'download-required'
          ) {
            // Try to install if API available
            if (typeof installAPI === 'function') {
              debug.log(
                `Attempting to install language pack for ${targetLang}...`,
              );
              const installed = await installAPI({
                langs: [targetLang],
                processLocally: true,
              });
              if (installed) {
                debug.log(
                  `Successfully installed language pack for ${targetLang}`,
                );
                return 'available';
              }
            }
            return 'unavailable';
          }

          return 'unavailable';
        } catch (err) {
          debug.warn(`Error checking availability for ${targetLang}:`, err);
          return 'error';
        }
      };

      // Try requested language first (e.g., 'en-US'), then fall back to base (e.g., 'en')
      const baseLang = lang.includes('-') ? lang.split('-')[0] : null;
      let effectiveLang = lang;

      const requestedResult = await tryLanguage(lang);
      if (requestedResult === 'available') {
        effectiveLang = lang;
        debug.log(`Using requested language: ${effectiveLang}`);
      } else if (baseLang && baseLang !== lang) {
        // Try base language as fallback
        debug.log(
          `Requested language '${lang}' unavailable, trying base '${baseLang}'...`,
        );
        const baseResult = await tryLanguage(baseLang);
        if (baseResult === 'available') {
          effectiveLang = baseLang;
          debug.log(`Using base language fallback: ${effectiveLang}`);
        } else if (requestedResult !== 'error') {
          // Both unavailable - show error
          setError({
            type: 'language-not-supported',
            message: `Language pack for '${lang}' (or '${baseLang}') is not available. Enable on-device speech recognition in Chrome settings.`,
          });
          debug.error('No language pack available for:', lang, 'or', baseLang);
          return;
        }
        // If requestedResult was 'error', continue anyway (API might not be reliable)
      } else if (requestedResult === 'unavailable') {
        // No base language to try
        setError({
          type: 'language-not-supported',
          message: `Language pack for '${lang}' is not available. Enable on-device speech recognition in Chrome settings.`,
        });
        debug.error('Language pack unavailable for:', lang);
        return;
      }
      // If 'error', continue anyway - older browsers/unreliable API

      // Re-check recognition is still valid after async operations (component may have unmounted)
      if (recognitionRef.current !== recognition) {
        debug.warn(
          'Recognition instance changed during availability check, aborting',
        );
        return;
      }

      // Update recognition language to what's actually available
      recognition.lang = effectiveLang;
      debug.log('Language availability check passed, using:', effectiveLang);
    } else {
      debug.log(
        'Skipping availability check - API not available or local processing not required',
      );
    }

    // Final check before starting - instance may have changed during async gap
    if (recognitionRef.current !== recognition) {
      debug.warn('Recognition instance changed, aborting start');
      return;
    }

    setError(null);
    isStoppingRef.current = false;

    try {
      recognition.start();
      setIsListening(true);
      isListeningRef.current = true;
      debug.log('Speech recognition started successfully');
    } catch (err) {
      // May throw if already started
      debug.warn('Speech recognition start error:', err);
    }
  }, [isSupported, lang, requireLocalProcessing]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    isStoppingRef.current = true;
    recognitionRef.current.stop();
    setIsListening(false);
    isListeningRef.current = false;
    setInterimTranscript('');
  }, []);

  const abortListening = useCallback(() => {
    if (!recognitionRef.current) return;

    isStoppingRef.current = true;
    recognitionRef.current.abort();
    setIsListening(false);
    setIsAudioStarted(false);
    setIsSpeechStarted(false);
    isListeningRef.current = false;
    setInterimTranscript('');
    debug.log('Recognition aborted');
  }, []);

  const clearTranscripts = useCallback(() => {
    setRecentTranscripts([]);
    setInterimTranscript('');
    setAlternatives([]);
  }, []);

  return {
    isSupported,
    isLocalProcessing,
    isListening,
    isAudioStarted,
    isSpeechStarted,
    interimTranscript,
    recentTranscripts,
    alternatives,
    startListening,
    stopListening,
    abortListening,
    clearTranscripts,
    error,
  };
};
